import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, Bookmark, ShoppingCart, Tag, ShoppingBag } from "lucide-react";
import EmptyState from "@/components/common/EmptyState";
import { formatPrice } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n/I18nProvider";

export default function CartPage() {
  const { t, currency } = useI18n();
  const { cart, cartSubtotal, updateQty, removeFromCart, saveForLater, savedForLater, moveToCart } = useApp();
  const [coupon, setCoupon] = useState("");
  const [couponMsg, setCouponMsg] = useState("");

  const applyCoupon = () => setCouponMsg(coupon ? t("connect_store") : "");

  if (cart.length === 0 && savedForLater.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <EmptyState icon={ShoppingBag} title={t("cart_empty_title")} body={t("cart_empty_body")} action={<Link to="/shop" className="rounded-full bg-ink px-6 py-2.5 text-sm font-semibold text-white">{t("continue_shopping")}</Link>} />
      </div>
    );
  }

  const cur = cart[0]?.currency || currency;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <h1 className="mb-6 font-display text-2xl font-bold text-ink sm:text-3xl">{t("cart_title")}</h1>
      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="divide-y divide-border rounded-2xl border border-border">
            {cart.map((line) => (
              <div key={line.key} className="flex gap-4 p-4" data-testid="cart-line">
                <Link to={`/product/${line.slug}`} className="h-24 w-24 shrink-0 overflow-hidden rounded-xl bg-secondary">
                  {line.image && <img src={line.image} alt={line.name} className="h-full w-full object-cover" />}
                </Link>
                <div className="flex flex-1 flex-col">
                  <Link to={`/product/${line.slug}`} className="line-clamp-2 text-sm font-medium text-ink hover:text-brand">{line.name}</Link>
                  {line.variant && <div className="mt-1 text-xs text-muted-foreground">{Object.entries(line.variant).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>}
                  <div className="mt-auto flex items-center justify-between pt-2">
                    <div className="flex items-center rounded-full border border-border">
                      <button onClick={() => updateQty(line.key, line.qty - 1)} className="grid h-8 w-8 place-items-center"><Minus size={14} /></button>
                      <span className="w-8 text-center text-sm font-semibold">{line.qty}</span>
                      <button onClick={() => updateQty(line.key, line.qty + 1)} className="grid h-8 w-8 place-items-center"><Plus size={14} /></button>
                    </div>
                    <span className="font-display font-bold text-ink">{formatPrice((line.price || 0) * line.qty, line.currency)}</span>
                  </div>
                  <div className="mt-2 flex gap-4 text-xs">
                    <button onClick={() => saveForLater(line.key)} className="flex items-center gap-1 text-muted-foreground hover:text-brand"><Bookmark size={13} /> {t("save_for_later")}</button>
                    <button onClick={() => removeFromCart(line.key)} className="flex items-center gap-1 text-muted-foreground hover:text-destructive" data-testid="cart-remove"><Trash2 size={13} /> {t("remove")}</button>
                  </div>
                </div>
              </div>
            ))}
            {cart.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">{t("cart_empty_title")}</div>}
          </div>

          {savedForLater.length > 0 && (
            <div className="mt-8">
              <h2 className="mb-3 font-display text-lg font-bold text-ink">{t("save_for_later")}</h2>
              <div className="divide-y divide-border rounded-2xl border border-border">
                {savedForLater.map((line) => (
                  <div key={line.key} className="flex items-center gap-4 p-4">
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-secondary">{line.image && <img src={line.image} alt={line.name} className="h-full w-full object-cover" />}</div>
                    <Link to={`/product/${line.slug}`} className="line-clamp-1 flex-1 text-sm font-medium hover:text-brand">{line.name}</Link>
                    <span className="text-sm font-semibold">{formatPrice(line.price, line.currency)}</span>
                    <button onClick={() => moveToCart(line.key)} className="flex items-center gap-1 rounded-full bg-ink px-3 py-1.5 text-xs font-semibold text-white"><ShoppingCart size={13} /> {t("add_to_cart")}</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="sticky top-28 rounded-2xl border border-border p-5">
            <h2 className="mb-4 font-display text-lg font-bold text-ink">{t("order_summary")}</h2>
            <div className="mb-4 flex gap-2">
              <div className="flex flex-1 items-center rounded-full border border-border ps-3">
                <Tag size={15} className="text-muted-foreground" />
                <input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder={t("coupon")} className="h-10 flex-1 bg-transparent px-2 text-sm outline-none" data-testid="coupon-input" />
              </div>
              <button onClick={applyCoupon} className="h-10 rounded-full bg-secondary px-4 text-sm font-semibold hover:bg-secondary/70" data-testid="coupon-apply">{t("apply")}</button>
            </div>
            {couponMsg && <p className="mb-4 text-xs text-muted-foreground">{couponMsg}</p>}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">{t("subtotal")}</span><span className="font-semibold">{formatPrice(cartSubtotal, cur)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("shipping")}</span><span className="text-muted-foreground">{t("shipping_calc")}</span></div>
            </div>
            <div className="mt-4 flex justify-between border-t border-border pt-4">
              <span className="font-display font-bold text-ink">{t("estimated_total")}</span>
              <span className="font-display text-xl font-extrabold text-ink">{formatPrice(cartSubtotal, cur)}</span>
            </div>
            <Link to="/checkout" className={`mt-5 flex h-12 items-center justify-center rounded-full bg-brand text-sm font-bold text-brand-foreground hover:brightness-105 ${cart.length === 0 ? "pointer-events-none opacity-40" : ""}`} data-testid="checkout-btn">
              {t("proceed_checkout")}
            </Link>
            <Link to="/shop" className="mt-3 block text-center text-sm font-semibold text-brand hover:underline">{t("continue_shopping")}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
